import { EventBus } from '../../plugins/event-bus'
import {
  callRunUpdates,
  callUpdateSettings,
  callSetWebsiteSettings,
} from '../../api/shared.js'
import { callGetPost } from '../../api/careerwise.js'
import { triggerErrorGeneric } from '../util.js'
import { POSTS_VIEW_OPTIONS } from '../../util/constants.js'

export async function dispatchUpdateIsLoading(context, value) {
  try {
    if (value) {
      context.commit('UPDATE_IS_LOADING')
    } else {
      context.commit('CLEAR_IS_LOADING')
    }
  } catch {
    triggerErrorGeneric()
  }
}

/**
 * Originally for Expo. Also for Careerwise.
 * Run updates without (or with) migrations.
 *
 * @param {Object} context state context
 * @param {Boolean} seedOnly flag to skip migrations
 */
export async function runUpdates(context, seedOnly = true) {
  try {
    const payload = {
      seed_only: seedOnly,
      tenant_id: context.state.settings.tenant_id,
    }

    const response = await callRunUpdates(payload)
    if (response.data?.success) {
      EventBus.$emit('reloadWebsiteSettings')
    }
  } catch {
    triggerErrorGeneric()
  }
}

/**
 * Get current (CareerWise tenant) post data.
 * @TODO: This should perhaps live in CareerWise store module as I
 * don't think it is used anywhere else (Expo and contributors handle their own).
 *
 * @param {Object} context state context
 * @param {Number} postId
 */
export async function dispatchUpdateCurrentPost(context, postId) {
  try {
    const response = await callGetPost(postId)
    const data = response.data?.success ? response.data.post : null
    context.dispatch('dispatchSetCurrentPost', data)
    context.dispatch('careerwise/dispatchLogPostView', data, { root: true })

    if (response.data.alert) {
      EventBus.$emit('triggerAlertResponse', response.data.alert)
    }
  } catch {
    triggerErrorGeneric()
  }
}

export async function dispatchUpdateSettings(_context, payload) {
  try {
    const response = await callUpdateSettings(payload)
    if (response.data.alert) {
      EventBus.$emit('triggerAlertResponse', response.data.alert)
    }

    if (payload.refresh) {
      window.location.reload()
    } else {
      EventBus.$emit('reloadWebsiteSettings')
    }
  } catch {
    triggerErrorGeneric()
  }
}

export async function dispatchSetWebsiteSettings(context) {
  try {
    const client = context.rootState.shared.client
    if (client) {
      const response = await callSetWebsiteSettings(client)
      const settings = response.data?.settings

      if (settings?.website) {
        // update website url
        settings.website_str = settings.website
          .replace('https://', '')
          .replace('http://', '')
      }

      // override primary colour variable
      if (settings?.primary_colour) {
        document.documentElement.style.setProperty(
          '--primary',
          settings.primary_colour,
        )
      }

      // override primary font variable
      if (settings?.font_title) {
        document.documentElement.style.setProperty(
          '--font_primary',
          settings.font_title,
        )
      }

      // override primary font variable
      if (settings?.font_body) {
        document.documentElement.style.setProperty(
          '--font_body',
          settings.font_body,
        )
      }

      context.commit('UPDATE_SETTINGS', settings)
      if (settings.events) {
        context.dispatch('expo/dispatchSetEvents', settings.events, {
          root: true,
        })
      }

      if (settings.customisation) {
        context.commit(
          'careerwise/UPDATE_CUSTOMISATION',
          settings.customisation,
          { root: true },
        )
      }

      if (settings.connections) {
        context.commit('careerwise/UPDATE_CONNECTIONS', settings.connections, {
          root: true,
        })
      }

      if (settings.feature_beta_access_tenant_ids) {
        const hasAccess = settings.feature_beta_access_tenant_ids.includes(
          settings.tenant_id,
        )
        context.commit('careerwise/UPDATE_BETA_FEATURE_ACCESS', hasAccess, {
          root: true,
        })
      }

      // @todo implement below in appropriate cases - search for EventBus.$on('websiteSettingsUpdated'
      // context.dispatch('careerwise/dispatchCheckForUpdates', null, { root: true })
      EventBus.$emit('websiteSettingsUpdated', settings)
    }
  } catch {
    triggerErrorGeneric()
  }
}

export function dispatchSetMenu(context, data) {
  try {
    if (data) {
      context.commit('UPDATE_MENU', data)
    }
  } catch {
    triggerErrorGeneric()
  }
}

export function dispatchSetClient(context, client) {
  try {
    if (client) {
      context.commit('UPDATE_CLIENT', client)
    }
  } catch {
    triggerErrorGeneric()
  }
}

export function dispatchSetUserSettings(context, data = null) {
  try {
    if (data) {
      if (data.user) {
        context.commit('UPDATE_USER', data.user)
      }

      if (data.userAccess) {
        context.commit('UPDATE_USER_ACCESS', data.userAccess)
      }
    } else {
      context.commit('CLEAR_USER')
      context.commit('CLEAR_USER_ACCESS')
    }
  } catch {
    triggerErrorGeneric()
  }
}

export function dispatchSetTemplateView(context, data) {
  try {
    if (data) {
      context.commit('UPDATE_TEMPLATE_VIEW', data)
    }
  } catch {
    triggerErrorGeneric()
  }
}

export function dispatchSetIdleTimerClosed(context, data) {
  try {
    context.commit('UPDATE_IDLE_TIMER_CLOSED', data)
  } catch {
    triggerErrorGeneric()
  }
}

export function dispatchSetCurrentPost(context, data = null) {
  try {
    if (data) {
      context.commit('UPDATE_CURRENT_POST', data)
    } else {
      context.commit('CLEAR_CURRENT_POST')
    }
  } catch {
    triggerErrorGeneric()
  }
}

export function dispatchSetCurrentPostResources(context, data = null) {
  try {
    if (data) {
      context.commit('UPDATE_CURRENT_POST_RESOURCES', data)
    } else {
      context.commit('CLEAR_CURRENT_POST_RESOURCES')
    }
  } catch {
    triggerErrorGeneric()
  }
}

export function dispatchSetCurrentPostsTemplateId(context, data) {
  try {
    context.commit('UPDATE_CURRENT_POSTS_TEMPLATE_ID', data)
  } catch {
    triggerErrorGeneric()
  }
}

export function dispatchSetPostsView(context, data) {
  try {
    let key = POSTS_VIEW_OPTIONS.DEFAULT

    if (data) {
      // check we are updating to a valid value
      const validKey = Object.keys(POSTS_VIEW_OPTIONS).find(
        (key) => POSTS_VIEW_OPTIONS[key] === data,
      )
      if (validKey) {
        key = validKey
      }
    }
    context.commit('UPDATE_POSTS_VIEW', POSTS_VIEW_OPTIONS[key])
  } catch {
    triggerErrorGeneric()
  }
}

export function dispatchSetFilterPostsBy(context, data) {
  try {
    context.commit('UPDATE_FILTER_POSTS_BY', data)
  } catch {
    triggerErrorGeneric()
  }
}
